import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionQuery } from '@app/store/session/session.query';
import { OpenService, VehicleFilter } from '@app-services/open.service';
import { PageControl } from '@app-model/page-control';
import { ShelfService } from '@app-services/shelf.service';
import { Vehicle } from '@app-model/vehicle';
import {
  PurchaseProfileOption,
  PurchaseProfileQuestion,
  PurchaseProfileQuestionId,
  DealerPurchaseProfileRequest,
  PurchaseProfileAnswers,
  PurchaseProfileVehicleFilter,
} from '../../purchase-profile.model';
import { PurchaseProfileService } from '../../purchase-profile.service';
import { CheckboxComponent } from '@app-components/ui/checkbox/checkbox.component';
import { RadioComponent } from '@app-components/ui/radio/radio.component';
import { DialogComponent } from '@app-components/ui/dialog/dialog.component';
import { PurchaseProfileVehicleCardComponent } from '../purchase-profile-vehicle-card/purchase-profile-vehicle-card.component';

interface QuestionSummaryRow {
  stepIndex: number;
  icon: string;
  label: string;
  value: string;
}

@Component({
  selector: 'app-purchase-profile-modal',
  standalone: true,
  imports: [
    CommonModule,
    CheckboxComponent,
    RadioComponent,
    DialogComponent,
    PurchaseProfileVehicleCardComponent,
  ],
  templateUrl: './purchase-profile-modal.component.html',
  styleUrls: ['./purchase-profile-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseProfileModalComponent {
  readonly service = inject(PurchaseProfileService);
  private readonly sessionQuery = inject(SessionQuery);
  private readonly destroyRef = inject(DestroyRef);
  private readonly openService = inject(OpenService);
  private readonly shelfService = inject(ShelfService);

  private advanceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastLoadedFilterKey: string | null = null;

  readonly matchingCount = signal<number>(0);
  readonly matchingVehicles = signal<Vehicle[]>([]);
  readonly isLoadingMatches = signal<boolean>(false);
  readonly canScrollSuggestionsPrev = signal(false);
  readonly canScrollSuggestionsNext = signal(false);

  @ViewChild('suggestionsTrack') suggestionsTrack?: ElementRef<HTMLElement>;

  readonly localAnswers = signal<PurchaseProfileAnswers>({ ...this.service.answers() });

  readonly questions = computed<PurchaseProfileQuestion[]>(() => this.service.questions());

  readonly isIntro = computed(() => this.service.currentStep() === 0);
  readonly isResult = computed(() => this.service.currentStep() === 6);
  readonly isQuestion = computed(() => this.service.currentStep() >= 1 && this.service.currentStep() <= 5);
  readonly isLastQuestion = computed(() => this.service.currentStep() === 5);
  readonly isReviewIntro = computed(
    () => this.isIntro() && (this.service.onboarding()?.status === 'COMPLETED' || !!this.service.savedAt())
  );

  readonly currentQuestion = computed<PurchaseProfileQuestion>(() => {
    const list = this.questions();
    const step = this.service.currentStep();
    const index = Math.max(0, Math.min(step - 1, list.length - 1));
    return list[index];
  });

  readonly showQuestionAction = computed(() => !!this.currentQuestion()?.multi || this.isLastQuestion());

  readonly progressPct = computed(() => {
    const step = this.service.currentStep();
    return `${(step / 5) * 100}%`;
  });

  readonly progressText = computed(() => {
    return `${this.service.currentStep()} de 5`;
  });

  readonly currentProfile = computed(() => this.service.currentProfile());

  readonly savedProfileName = computed(() => this.service.profileType()?.name || 'Perfil salvo');

  readonly storeInfoDisplay = computed(() => {
    const city = this.service.dealerCity()?.name;
    const uf = this.service.dealerUf();
    const user = this.sessionQuery.getValue()?.userInfo;
    const storeName = user?.dealerName || user?.dealers?.[0]?.name || user?.name || 'Sua Loja';

    if (city && uf) {
      return `${storeName} · ${city}/${uf}`;
    }
    return `${storeName} · Concessionária / Multimarcas`;
  });

  readonly summaryRows = computed<QuestionSummaryRow[]>(() => {
    const a = this.localAnswers();
    const list = this.questions();

    return list.map((q, i) => {
      let selectedValues: string[] = [];
      if (q.multi) {
        selectedValues = (a[q.id] as string[]) || [];
      } else {
        const val = a[q.id] as string | undefined;
        if (val) selectedValues = [val];
      }

      const labels = q.options
        .filter((opt) => selectedValues.includes(opt.id))
        .map((opt) => opt.label);

      const text = labels.length > 0 ? labels.join(', ') + (q.summarySuffix || '') : 'Sem preferência';
      return {
        stepIndex: i + 1,
        icon: q.icon,
        label: q.label,
        value: text,
      };
    });
  });

  constructor() {
    effect(
      () => {
        const serverAnswers = this.service.answers();
        this.localAnswers.set({ ...serverAnswers });
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const step = this.service.currentStep();
        if (step === 6) {
          const filter = this.service.vehicleFilter();
          if (!filter) {
            this.lastLoadedFilterKey = null;
            this.matchingCount.set(0);
            this.matchingVehicles.set([]);
            this.isLoadingMatches.set(false);
            return;
          }
          const filterKey = JSON.stringify(filter);
          if (this.lastLoadedFilterKey !== filterKey) {
            this.lastLoadedFilterKey = filterKey;
            this.loadMatchingVehicles(filter);
          }
        } else {
          this.lastLoadedFilterKey = null;
        }
      },
      { allowSignalWrites: true }
    );

    this.destroyRef.onDestroy(() => {
      this.clearAdvanceTimeout();
    });
  }

  loadMatchingVehicles(vehicleFilter: PurchaseProfileVehicleFilter): void {
    this.isLoadingMatches.set(true);
    const searchFilter = Object.assign(new VehicleFilter(), vehicleFilter);
    searchFilter.pageControl = Object.assign(new PageControl(), {
      page: 0,
      pageSize: 4,
      sortField: 'score',
      sortDirection: 'DESC',
    });

    this.openService.searchAuctions(searchFilter).subscribe({
      next: (resp) => {
        this.matchingCount.set(resp?.totalElements ?? 0);
        this.matchingVehicles.set(resp?.data ?? []);
        this.isLoadingMatches.set(false);
        this.canScrollSuggestionsPrev.set(false);
        this.canScrollSuggestionsNext.set((resp?.data?.length ?? 0) > 2);
        setTimeout(() => this.updateSuggestionArrows(), 48);
      },
      error: () => {
        this.matchingCount.set(0);
        this.matchingVehicles.set([]);
        this.isLoadingMatches.set(false);
      },
    });
  }

  onSuggestionsScroll(): void {
    this.updateSuggestionArrows();
  }

  scrollSuggestions(direction: -1 | 1): void {
    const track = this.suggestionsTrack?.nativeElement;
    const item = track?.firstElementChild as HTMLElement | null;
    if (!track || !item) {
      return;
    }

    const step = item.getBoundingClientRect().width + 16;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  private updateSuggestionArrows(): void {
    const track = this.suggestionsTrack?.nativeElement;
    if (!track) {
      this.canScrollSuggestionsPrev.set(false);
      this.canScrollSuggestionsNext.set(false);
      return;
    }

    const maxScroll = track.scrollWidth - track.clientWidth;
    this.canScrollSuggestionsPrev.set(track.scrollLeft > 4);
    this.canScrollSuggestionsNext.set(maxScroll > 4 && track.scrollLeft < maxScroll - 4);
  }

  isOptionSelected(optionId: string): boolean {
    const q = this.currentQuestion();
    const a = this.localAnswers();

    if (q.multi) {
      const current = (a[q.id] as string[]) || [];
      return current.includes(optionId);
    }

    return (a[q.id] as string | undefined) === optionId;
  }

  toggleOption(option: PurchaseProfileOption): void {
    const q = this.currentQuestion();
    const answers = { ...this.localAnswers() };

    if (q.multi) {
      const current = [...((answers[q.id] as string[]) || [])];
      const idx = current.indexOf(option.id);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(option.id);
      }
      this.assignQuestionAnswer(q.id, current, answers);
      this.localAnswers.set(answers);
    } else {
      this.assignQuestionAnswer(q.id, option.id, answers);
      this.localAnswers.set(answers);

      if (this.isLastQuestion()) {
        return;
      }

      this.clearAdvanceTimeout();
      this.advanceTimeoutId = setTimeout(() => {
        this.nextStep();
      }, 320);
    }
  }

  canProceedCurrentQuestion(): boolean {
    const q = this.currentQuestion();
    const a = this.localAnswers();

    if (q.multi) {
      const selected = (a[q.id] as string[]) || [];
      return selected.length > 0;
    }

    return !!(a[q.id] as string | undefined);
  }

  getQuestionSummary(): string {
    const q = this.currentQuestion();
    const a = this.localAnswers();
    const selected = (a[q.id] as string[]) || [];
    const labels = q.options.filter((opt) => selected.includes(opt.id)).map((opt) => opt.label);
    if (!labels.length) return '';
    return labels.join(', ') + (q.summarySuffix || '');
  }

  nextStep(): void {
    const step = this.service.currentStep();
    if (step === 5) {
      this.saveAndGoToResult();
      return;
    }
    if (step < 6) {
      this.service.setStep(step + 1);
    }
  }

  private saveAndGoToResult(): void {
    this.service.saveProfile(this.toRequest(this.localAnswers())).subscribe((res) => {
      if (res) {
        this.shelfService.clearShelfCache('selected-for-you');
        this.service.setStep(6);
      }
    });
  }

  previousStep(): void {
    const step = this.service.currentStep();
    if (step > 0) {
      this.service.setStep(step - 1);
    }
  }

  goToStep(step: number): void {
    this.service.setStep(step);
  }

  dismissModal(): void {
    if (this.service.shouldRegisterSkip()) {
      this.service.skipProfile().subscribe();
    }
    this.service.closeModal();
  }

  continueFromResult(): void {
    const answers = this.localAnswers();
    const saved = this.service.answers();
    const hasChanges = JSON.stringify(answers) !== JSON.stringify(saved);

    if (!hasChanges) {
      this.finishResult();
      return;
    }

    this.service.saveProfile(this.toRequest(answers)).subscribe((res) => {
      if (!res) {
        return;
      }
      this.shelfService.clearShelfCache('selected-for-you');
      this.finishResult();
    });
  }

  private finishResult(): void {
    this.service.closeModal();
    this.service.notifyProfileSaved();
  }

  private toRequest(answers: PurchaseProfileAnswers): DealerPurchaseProfileRequest {
    return {
      vehicleTypes: answers.vehicleTypes || [],
      priceRanges: answers.priceRanges || [],
      fipeDiscountRanges: answers.fipeDiscountRanges || [],
      region: answers.region || '',
      vehicleCondition: answers.vehicleCondition || '',
    };
  }

  private clearAdvanceTimeout(): void {
    if (this.advanceTimeoutId) {
      clearTimeout(this.advanceTimeoutId);
      this.advanceTimeoutId = null;
    }
  }

  private assignQuestionAnswer(
    questionId: PurchaseProfileQuestionId,
    value: string[] | string,
    target: PurchaseProfileAnswers
  ): void {
    switch (questionId) {
      case 'vehicleTypes':
        target.vehicleTypes = value as string[];
        break;
      case 'priceRanges':
        target.priceRanges = value as string[];
        break;
      case 'fipeDiscountRanges':
        target.fipeDiscountRanges = value as string[];
        break;
      case 'region':
        target.region = value as string;
        break;
      case 'vehicleCondition':
        target.vehicleCondition = value as string;
        break;
    }
  }
}
